



















import { getMyMonthlyTasksApi, getMonthlyLeaderboardApi, getAllMonthlyReportsApi, completeMonthlyTaskApi, setEmployeeOfTheMonthApi } from "../../../app/api";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp, Trophy, CheckCircle2, AlertTriangle, Star, MinusCircle, Calendar, Users, Target, Info, Search, Filter, X, Check } from "lucide-react";
import toast from "react-hot-toast";
import { useEffect, useState, useMemo } from "react";

import { createPortal } from "react-dom";

const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

export default function MonthlyTaskAccuracy({ defaultView = 'tasks' }) {
  const [userStats, setUserStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedTask, setExpandedTask] = useState(null);
  const [allReports, setAllReports] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [view, setView] = useState(defaultView); // 'tasks' or 'leaderboard'
  const [showStarsModal, setShowStarsModal] = useState(false);
  const [selectedTopUser, setSelectedTopUser] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const loggedUserId = Number(user?.id);
  const isAdmin = [1, 2, 3].includes(Number(user?.role_id));

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingPerformer, setPendingPerformer] = useState(null);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const currentMonthLabel = `${monthNames[selectedMonth - 1]} ${selectedYear}`;

  const load = async () => {
    setLoading(true);
    try {
      const [leaderRes, myTasksRes, reportsRes] = await Promise.all([
        getMonthlyLeaderboardApi(selectedMonth, selectedYear),
        getMyMonthlyTasksApi(selectedMonth, selectedYear),
        getAllMonthlyReportsApi(selectedMonth, selectedYear)
      ]);

      if (leaderRes.success) {
        setUserStats(leaderRes.data.map(u => {
          const totalTasks = u.completed_tasks + (u.active_tasks || 0) + (u.overdue_tasks || 0);
          const completion = totalTasks > 0 ? Math.round((u.completed_tasks / totalTasks) * 100) : 0;
          return {
            id: u.user_id,
            name: u.user_name,
            pic: u.profile_pic,
            points: u.pure_task_points,
            final_score: u.final_score,
            penalty: u.total_penalty,
            late_days: u.late_days || 0,
            early_out_days: u.early_out_days || 0,
            excess_break_days: u.excess_break_days || 0,
            done: u.completed_tasks,
            active: u.active_tasks || 0,
            overdue: u.overdue_tasks || 0,
            accuracy: u.accuracy || 0,
            completion: completion
          };
        }).sort((a, b) => b.final_score - a.final_score));
      }

      if (myTasksRes.success) setMyTasks(myTasksRes.data);
      if (reportsRes.success) setAllReports(reportsRes.data);

    } catch (err) {
      console.error("Failed to load monthly task data", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    setView(defaultView);
  }, [defaultView]);

  const handleComplete = async (id) => {
    try {
      const res = await completeMonthlyTaskApi(id);
      if (res.success) load();
    } catch (err) {
      console.error("Completion failed", err);
    }
  };

  const handleSelectEOM = async (performer) => {
    if (!isAdmin) return;
    setPendingPerformer(performer);
    setShowConfirm(true);
  };

  const confirmSelection = async () => {
    if (!pendingPerformer) return;

    try {
      const res = await setEmployeeOfTheMonthApi({
        userId: pendingPerformer.id,
        month: selectedMonth,
        year: selectedYear
      });

      if (res.success) {
        toast.success(`${pendingPerformer.name} selected as Employee of the Month!`);
        setShowConfirm(false);
        setPendingPerformer(null);
      } else {
        toast.error(res.message || "Failed to set Employee of the Month");
      }
    } catch (err) {
      console.error("EOM selection failed", err);
      toast.error(err.message || "Something went wrong");
    }
  };


  const myStats = userStats.find(s => s.id === loggedUserId) || { points: 0, penalty: 0, done: 0, accuracy: 0 };
  const top3 = userStats.slice(0, 3);
  const rest = userStats.slice(3);

  const today = new Date().toISOString().split("T")[0];

  const getAccuracyColors = (acc) => {
    if (acc >= 80) return { bar: "from-green-400 to-emerald-500", text: "text-green-400", badge: "bg-green-500/20 text-green-400 border-green-500/30", label: "Elite" };
    if (acc >= 60) return { bar: "from-yellow-400 to-amber-500", text: "text-yellow-400", badge: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "Gold" };
    if (acc >= 40) return { bar: "from-orange-400 to-red-400", text: "text-orange-400", badge: "bg-orange-500/20 text-orange-400 border-orange-500/30", label: "Average" };
    return { bar: "from-red-500 to-pink-500", text: "text-red-400", badge: "bg-red-500/20 text-red-400 border-red-500/30", label: "Needs Work" };
  };

  const renderUserTasks = (targetUser) => {
    return (
      <div className="space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar w-full text-left">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Done Tasks */}
          <div className="space-y-3">
            <h5 className="text-[10px] font-black text-green-400/60 uppercase tracking-[2px] flex items-center gap-2 mb-4">
              <CheckCircle2 size={12} /> Completed with Points
            </h5>
            {allReports.filter(r => r.user_id === targetUser.id && r.status === 'DONE').length > 0 ? (
              allReports.filter(r => r.user_id === targetUser.id && r.status === 'DONE').map(report => {
                const isTaskExpanded = expandedTask === report.id;
                const basePts = report.priority === 'HIGH' ? 3 : report.priority === 'MEDIUM' ? 2 : report.priority === 'URGENT' ? 4 : 1;
                const pureTaskScore = report.points_earned + (report.early_late_days || 0);
                const deadlineBonus = pureTaskScore - basePts;
                
                return (
                <div key={report.id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden transition hover:border-green-500/30">
                  <div 
                    onClick={(e) => { e.stopPropagation(); setExpandedTask(isTaskExpanded ? null : report.id); }}
                    className="p-4 flex items-center justify-between cursor-pointer group/task"
                  >
                    <div>
                      <p className="text-white/90 text-sm font-bold truncate max-w-[150px]">{report.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[9px] text-white/40 font-bold uppercase">{report.priority}</span>
                        <span className="w-1 h-1 rounded-full bg-white/10" />
                        <span className="text-[9px] text-green-400 font-bold">{report.due_date?.split('T')[0]}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className={`font-black text-lg ${pureTaskScore >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {pureTaskScore > 0 ? '+' : ''}{pureTaskScore}
                        </div>
                        <div className="text-[9px] text-white/20 uppercase font-black">Points</div>
                      </div>
                      <div className={`p-1 rounded-full bg-white/5 text-white/40 transition-transform ${isTaskExpanded ? 'rotate-180' : ''}`}>
                        <ChevronDown size={14} />
                      </div>
                    </div>
                  </div>
                  <AnimatePresence>
                    {isTaskExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-white/10 bg-black/40"
                      >
                        <div className="p-4 space-y-2 text-xs">
                          <div className="flex justify-between items-center text-white/60">
                            <span>Base Points ({report.priority})</span>
                            <span className="font-bold text-white">+{basePts}</span>
                          </div>
                          <div className="flex justify-between items-center text-white/60">
                            <span>Deadline Bonus</span>
                            <span className="font-bold text-white">{deadlineBonus >= 0 ? '+' : ''}{deadlineBonus}</span>
                          </div>
                          <div className="h-px bg-white/10 my-2" />
                          <div className="flex justify-between items-center">
                            <span className="font-black text-white/80 uppercase text-[10px] tracking-widest">Final Task Score</span>
                            <span className={`font-black ${pureTaskScore >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {pureTaskScore > 0 ? '+' : ''}{pureTaskScore}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )})
            ) : (
              <p className="text-white/10 text-xs italic text-center py-4">No completed tasks found</p>
            )}
          </div>

          {/* Active/Simple Tasks */}
          <div className="space-y-3">
            <h5 className="text-[10px] font-black text-indigo-400/60 uppercase tracking-[2px] flex items-center gap-2 mb-4">
              <Users size={12} /> Pending / Active Tasks
            </h5>
            <div className="space-y-2">
              {allReports.filter(r => r.user_id === targetUser.id && r.status !== 'DONE').length > 0 ? (
                allReports.filter(r => r.user_id === targetUser.id && r.status !== 'DONE').map(task => (
                  <div key={task.id} className="bg-white/5 border border-white/10 rounded-2xl p-3 flex items-center justify-between group/active hover:border-indigo-500/30 transition">
                    <div className="flex-1 min-w-0">
                      <p className="text-white/80 text-xs font-bold truncate">{task.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[8px] font-black uppercase ${task.due_date && new Date(task.due_date) < new Date() ? 'text-red-400' : 'text-indigo-400'}`}>
                          {task.due_date && new Date(task.due_date) < new Date() ? 'Overdue' : 'On Track'}
                        </span>
                        <span className="w-0.5 h-0.5 rounded-full bg-white/10" />
                        <span className="text-[8px] text-white/20 uppercase font-black">Due: {task.due_date?.split('T')[0]}</span>
                      </div>
                    </div>
                    <span className="text-[8px] px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-400 font-black border border-indigo-500/20">
                      {task.status}
                    </span>
                  </div>
                ))
              ) : (
                <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-3xl p-6 text-center">
                  <div className="text-2xl font-black text-white/20 mb-1">0</div>
                  <p className="text-[9px] text-white/20 font-bold uppercase tracking-widest">No Active Tasks</p>
                </div>
              )}
            </div>
          </div>
          {/* Penalties Section */}
          <div className="space-y-3 col-span-1 md:col-span-2 mt-2">
            <h5 className="text-[10px] font-black text-red-400/60 uppercase tracking-[2px] flex items-center gap-2 mb-4">
              <AlertTriangle size={12} /> Penalties & Deductions
            </h5>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 text-center">
                <div className="text-red-400 font-black text-xl mb-1">{targetUser.late_days}</div>
                <div className="text-[9px] text-white/40 uppercase font-bold">Late Days</div>
              </div>
              <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 text-center">
                <div className="text-red-400 font-black text-xl mb-1">{targetUser.early_out_days}</div>
                <div className="text-[9px] text-white/40 uppercase font-bold">Early Out</div>
              </div>
              <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 text-center">
                <div className="text-red-400 font-black text-xl mb-1">{targetUser.excess_break_days}</div>
                <div className="text-[9px] text-white/40 uppercase font-bold">Excess Break</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-10 h-10 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin shadow-[0_0_15px_rgba(99,102,241,0.5)]" />
      </div>
    );
  }

  return (
    <>
      <div className="mt-6 w-full">

        {/* HEADER & FILTERS */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6
         bg-[#0B051D] backdrop-blur-xl border border-white/10 p-8 md:p-8
          rounded-[20px] shadow-2xl relative overflow-hidden">



          {/* relative
 w-full
 bg-gradient-to-br from-[#140c2b] via-[#1f1147] to-[#0f0825]
 border border-white/10
 p-6 rounded-[24px] md:mt-[-4px]
 overflow-hidden no-scrollbar mt-[-64px]
 shadow-2xl */}

          <div className="absolute top-0 left-0 w-64 h-64 md:mt-[-24px] bg-indigo-500/10 rounded-sm blur-[100px] -translate-x-1/2 -translate-y-1/2" />
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-semibold text-white flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-md border border-indigo-500/30">
                <Target className="text-indigo-400" size={22} />
              </div>
              <span className="bg-gradient-to-r from-white via-indigo-100 to-indigo-300 bg-clip-text text-transparent">Performance Analytics</span>
            </h2>
            {/* <p className="text-indigo-200/60 text-sm md:text-base font-medium mt-2 max-w-lg">Track efficiency, accuracy, and task completion metrics with precision analytics.</p> */}
          </div>

          <div className="flex flex-wrap items-center gap-4 relative z-10 w-full lg:w-auto">
            {/* VIEW TOGGLE */}


            <div className="flex items-center bg-black/40 border border-white/10 rounded-2xl p-1 w-full sm:w-auto">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="bg-transparent text-white text-sm font-bold px-4 py-2 outline-none cursor-pointer hover:text-indigo-400 transition flex-1 sm:flex-none"
              >
                {monthNames.map((m, i) => (
                  <option key={m} value={i + 1} className="bg-[#12082F]">{m}</option>
                ))}
              </select>
              <div className="w-px h-6 bg-white/10" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="bg-transparent text-white text-sm font-bold px-4 py-2 outline-none cursor-pointer hover:text-indigo-400 transition flex-1 sm:flex-none"
              >
                {[2024, 2025, 2026].map(y => (
                  <option key={y} value={y} className="bg-[#12082F]">{y}</option>
                ))}
              </select>
            </div>

            <button
              onClick={() => setShowInstructions(true)}
              className="w-full sm:w-auto bg-white/5 hover:bg-white/10 text-white font-bold text-xs px-6 py-3 rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-95 border border-white/10"
            >
              <Info size={16} className="text-indigo-400" />
              INSTRUCTIONS
            </button>

            <button
              onClick={() => setShowStarsModal(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-black font-black text-xs px-6 py-3 rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-yellow-500/20"
            >
              <Trophy size={16} />
              TOP PERFORMERS
            </button>
          </div>
        </div>

        {/* TASKS VIEW */}
        {view === 'tasks' && (
          <div className="space-y-8">
            {/* MY PERFORMANCE SUMMARY */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1 md:gap-2 md:mt-4">

              {[
                { label: "My Total Tasks", val: myTasks.length, color: "indigo", icon: Target },
                { label: "Completed", val: myStats.done, color: "green", icon: CheckCircle2 },
                { label: "Total Points", val: `${myStats.points} Pts`, color: "yellow", icon: Star },
                { label: "Accuracy", val: `${myStats.accuracy}%`, color: "purple", icon: Target }
              ].map((card, i) => (
                <div key={i} className="bg-[#130931] backdrop-blur-xl border border-white/10 rounded-[14px] p-5 md:p-6 shadow-xl relative overflow-hidden group hover:border-white/20 transition-all duration-500">
                  <div className={`absolute -right-4 -bottom-4 w-20 h-20 bg-${card.color}-500/10 rounded-full blur-2xl group-hover:bg-${card.color}-500/20 transition-all duration-500`} />
                  <div className="flex flex-col h-full justify-between gap-4">
                    <div className={`w-10 h-10 rounded-xl bg-${card.color}-500/10 flex items-center justify-center border border-${card.color}-500/20`}>
                      <card.icon className={`text-${card.color}-400`} size={20} />
                    </div>
                    <div>
                      <h4 className="text-white/40 text-[9px] md:text-[10px] uppercase font-black tracking-[2px] mb-1">{card.label}</h4>
                      <div className="text-2xl md:text-3xl font-semibold text-white">{card.val}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* MY TASKS SECTION */}
            {/* <div className="relative overflow-hidden rounded-[40px] p-6 md:p-10 bg-[#0B051D] backdrop-blur-3xl text-white border border-white/10 shadow-2xl">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-500/[0.03] rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-purple-500/[0.03] rounded-full blur-[100px] pointer-events-none" /> */}







            <div className="relative overflow-hidden rounded-[14px] md:mt-[-34px] p-8 md:p-8 bg-[#0B051D] text-white border border-white/5 shadow-[0_30px_100px_-20px_rgba(11,5,29,0.9)]">

              {/* Glow Effects */}
              <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-600/20 rounded-full blur-[100px] pointer-events-none" />
              <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[100px] pointer-events-none" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.02)_0%,transparent_80%)] pointer-events-none" />

              <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4 relative z-10">
                <h3 className="text-2xl md:text-3xl font-semibold flex items-center gap-3">
                  <div className="p-2 bg-indigo-500/20 rounded-xl">
                    <CheckCircle2 className="text-indigo-400 font-semibold" size={24} />
                  </div>
                  My Monthly Tasks
                  <span className="text-indigo-400/40 text-lg font-bold">({currentMonthLabel})</span>
                </h3>
                <div className="px-4 py-2 bg-white/5 rounded-2xl border border-white/10 text-xs font-semibold text-white/40 uppercase tracking-widest">
                  {myTasks.length} Total Tasks
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
                {myTasks.length > 0 ? myTasks.map(task => (
                  <div key={task.id} className={`group p-6 rounded-[32px] border transition-all duration-500 ${task.status === 'DONE'
                    ? "bg-green-500/5 border-green-500/20 hover:border-green-500/40"
                    : "bg-white/5 border-white/10 hover:border-indigo-500/40 hover:bg-white/[0.08]"
                    }`}>
                    <div className="flex justify-between items-start mb-6">
                      <span className={`text-[10px] px-3 py-1.5 rounded-xl font-semibold uppercase tracking-widest border ${task.priority === 'HIGH' ? "bg-red-500/10 text-red-400 border-red-500/20" :
                        task.priority === 'MEDIUM' ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" :
                          "bg-blue-500/10 text-blue-400 border-blue-500/20"
                        }`}>
                        {task.priority}
                      </span>
                      {task.status === 'DONE' && (
                        <div className="flex items-center gap-1.5 text-green-400 text-sm font-black bg-green-400/10 px-3 py-1 rounded-full">
                          <Star size={14} className="fill-current" /> +{task.points_earned || 0}
                        </div>
                      )}
                    </div>

                    <h4 className="font-semibold text-xl text-white/90 mb-3 group-hover:text-white transition-colors">{task.title}</h4>
                    <p className="text-white/50 text-sm mb-8 line-clamp-2 leading-relaxed group-hover:text-white/70 transition-colors">{task.description}</p>

                    <div className="flex items-center justify-between mt-auto pt-6 border-t border-white/5">
                      <div className="flex flex-col gap-1">
                        <span className="text-[9px] text-white/20 uppercase font-semibold tracking-widest">Deadline</span>
                        <span className={`text-[12px] font-semibold ${task.is_overdue && task.status !== 'DONE' ? 'text-red-400' : 'text-white/60'}`}>
                          {task.due_date ? task.due_date.split('T')[0] : 'No Deadline'}
                        </span>
                      </div>
                      {task.status !== 'DONE' ? (
                        <button
                          onClick={() => handleComplete(task.id)}
                          className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-6 py-2.5 rounded-2xl transition-all shadow-lg shadow-indigo-600/20 active:scale-95 border border-indigo-400/30"
                        >
                          Mark Done
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 text-green-400 text-xs font-semibold bg-green-400/10 px-4 py-2 rounded-2xl border border-green-500/20">
                          <CheckCircle2 size={14} /> COMPLETED
                        </div>
                      )}
                    </div>
                  </div>
                )) : (
                  <div className="col-span-full py-24 text-center bg-white/[0.01] border border-white/5 rounded-[40px]">
                    <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/10">
                      <Target className="text-white/10" size={40} />
                    </div>
                    <h3 className="text-xl font-bold text-white/40 italic">No tasks found for this month</h3>
                    <p className="text-white/20 text-sm mt-2">All tasks for {currentMonthLabel} will appear here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* LEADERBOARD VIEW */}
        {view === 'leaderboard' && (
          <div className="relative overflow-hidden rounded-[24px] mt-4 p-4 sm:p-6 md:p-10 bg-[#0B051D] text-white border border-white/5 shadow-[0_30px_100px_-20px_rgba(11,5,29,0.9)]">
            {/* Glow Effects */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-indigo-600/20 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[100px] pointer-events-none" />

            <div className="relative z-10  flex flex-col md:flex-row items-start md:items-center justify-between mb-10 gap-4">
              <div>
                <h3 className="text-3xl font-black bg-gradient-to-r from-white via-indigo-200 to-purple-300 bg-clip-text text-transparent drop-shadow-sm">
                  Monthly Leaderboard
                </h3>
                <p className="text-indigo-200/60 font-medium mt-1 text-sm uppercase tracking-widest">
                  {currentMonthLabel} <span className="mx-2">•</span> {userStats.length} Employees Active
                </p>
              </div>
              <div className="flex items-center gap-2 bg-white/5 backdrop-blur-md border border-white/10 px-4 py-2 rounded-2xl shadow-lg">
                <Target size={16} className="text-indigo-400" />
                <span className="text-xs font-bold text-white/80 uppercase tracking-wide">Ranked by Points</span>
              </div>
            </div>

            {userStats.length === 0 ? (
              <div className="relative z-10 text-center py-20 text-white/30">
                <CheckCircle2 size={50} className="mx-auto mb-4 opacity-20" />
                <p className="text-lg font-medium">No task data available for {currentMonthLabel}</p>
              </div>
            ) : (
              <div className="relative z-10 space-y-12">

                {/* PODIUM */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-end">
                  {top3.map((user, index) => {
                    const isFirst = index === 0;
                    const isSecond = index === 1;
                    const isThird = index === 2;

                    // const cardColors = isFirst
                    //   ? "from-yellow-600/20 via-yellow-500/10 to-[#12082F] border-yellow-500/30 shadow-[0_0_30px_rgba(234,179,8,0.15)]"
                    //   : isSecond
                    //     ? "from-slate-400/20 via-slate-400/10 to-[#12082F] border-slate-400/30"
                    //     : "from-amber-700/20 via-amber-700/10 to-[#12082F] border-amber-700/30";



                    const cardColors = isFirst
                      ? "from-yellow-600/20 via-yellow-500/10 to-[#12082F] border-yellow-500/30 shadow-[0_0_30px_rgba(234,179,8,0.15)]"
                      : isSecond
                        ? "from-slate-400/20 via-slate-400/10 to-[#12082F] border-slate-400/30"
                        : "from-amber-700/20 via-amber-700/10 to-[#12082F] border-amber-700/30";


                    // const ringColors = isFirst ? "ring-yellow-400 shadow-[0_0_20px_rgba(250,204,21,0.5)]"
                    //   : isSecond ? "ring-slate-300 shadow-[0_0_20px_rgba(203,213,225,0.4)]"
                    //     : "ring-amber-600 shadow-[0_0_20px_rgba(217,119,6,0.4)]";

                    // const rankText = isFirst ? "text-yellow-400" : isSecond ? "text-slate-300" : "text-amber-500";
                    // const rankBg = isFirst ? "bg-yellow-500/20" : isSecond ? "bg-slate-400/20" : "bg-amber-700/20";


                    const ringColors = isFirst ? "ring-yellow-400 shadow-[0_0_20px_rgba(250,204,21,0.5)]"
                      : isSecond ? "ring-slate-300 shadow-[0_0_20px_rgba(203,213,225,0.4)]"
                        : "ring-amber-600 shadow-[0_0_20px_rgba(217,119,6,0.4)]";

                    const rankText = isFirst ? "text-yellow-400" : isSecond ? "text-slate-300" : "text-amber-500";
                    const rankBg = isFirst ? "bg-yellow-500/20" : isSecond ? "bg-slate-400/20" : "bg-amber-700/20";





                    return (
                      // <motion.div
                      //   key={user.id}
                      //   initial={{ opacity: 0, y: 30 }}
                      //   animate={{ opacity: 1, y: 0 }}
                      //   transition={{ delay: index * 0.15, type: "spring" }}
                      // className={`relative flex flex-col items-center group transition-all duration-500 ${isFirst ? 'scale-110 md:-translate-y-6 z-10' : ''}`}
                      // >


                      <motion.div
                        key={user.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1, type: "spring" }}
                        onClick={() => setSelectedTopUser(user)}
                        className={`cursor-pointer relative bg-gradient-to-b ${cardColors} rounded-[30px] p-6 border backdrop-blur-xl flex flex-col items-center text-center overflow-hidden group transition-all duration-500 ${isFirst ? 'sm:scale-110 sm:-translate-y-6 z-10' : ''}`}
                      >
                        <div className={`absolute top-4 left-4 w-8 h-8 rounded-full ${rankBg} flex items-center justify-center font-black text-sm ${rankText} border border-current/20 z-20`}>
                          {index + 1}
                        </div>

                        {/* <div className={`w-full bg-gradient-to-b ${cardColors} border border-white/10 rounded-[40px] p-8 backdrop-blur-xl text-center transition-all duration-500 hover:border-indigo-500/40 hover:shadow-2xl hover:shadow-indigo-500/10`}> */}

                        {/* Name First as requested earlier */}
                        {/* <h4 className="text-xl font-bold text-white mb-6 truncate px-2 group-hover:text-indigo-400 transition-colors uppercase tracking-wider">{user.name}</h4>

                        <div className="relative mb-6 mx-auto w-28 h-28">
                          <div className={`absolute inset-0 rounded-full blur-xl opacity-30 ${isFirst ? 'bg-yellow-500' : 'bg-indigo-500'}`} />
                          {user.pic ? (
                            <img src={`${IMAGE_BASE_URL}${user.pic}`} alt={user.name} className={`w-full h-full rounded-full object-cover ring-4 ${ringColors} ring-offset-4 ring-offset-[#0B051D]`} />
                          ) : (
                            <div className={`w-full h-full rounded-full flex items-center justify-center text-3xl font-black bg-[#1A123C] ring-4 ${ringColors} ring-offset-4 ring-offset-[#0B051D] ${isFirst ? 'text-yellow-400' : 'text-white/20'}`}>
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          {isFirst && <Trophy className="absolute -bottom-2 -right-2 text-yellow-400 drop-shadow-md bg-[#0B051D] rounded-full p-1 w-8 h-8" />}
                        </div>

                        <div className={`text-4xl font-black ${rankText} drop-shadow-lg mb-1`}>
                          {user.accuracy}%
                        </div> */}







                        <div className="relative mt-4 mb-5">
                          {user.pic ? (
                            <img src={`${IMAGE_BASE_URL}${user.pic}`} alt={user.name} className={`w-24 h-24 rounded-full object-cover ring-4 ${ringColors} ring-offset-4 ring-offset-[#0B051D]`} />
                          ) : (
                            <div className={`w-24 h-24 rounded-full bg-[#1A123C] flex items-center justify-center text-3xl font-black text-white ring-4 ${ringColors} ring-offset-4 ring-offset-[#0B051D]`}>
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          {isFirst && <Trophy className="absolute -bottom-2 -right-2 text-yellow-400 drop-shadow-md bg-[#0B051D] rounded-full p-1 w-8 h-8" />}
                        </div>

                        <h4 className="text-xl font-bold text-white mb-1">{user.name}</h4>
                        {isAdmin && (
                          <div className={`text-3xl font-black ${rankText} drop-shadow-lg mb-6`}>{user.final_score} <span className="text-sm">PTS</span></div>
                        )}
                        {/* <div className="text-[10px] font-black text-white/40 uppercase tracking-[2px] mb-6">
                          Accuracy Score
                        </div> */}

                        {/* <div className="grid grid-cols-3 gap-2 w-full pt-4 border-t border-white/5">
                          <div className="bg-black/30 rounded-2xl py-2 px-1 border border-white/5">
                            <div className="text-green-400 font-black text-sm">{user.done}</div>
                            <div className="text-[8px] text-white/30 uppercase font-black tracking-widest mt-0.5">Done</div>
                          </div>
                          <div className="bg-black/30 rounded-2xl py-2 px-1 border border-white/5">
                            <div className="text-indigo-400 font-black text-sm">{user.active}</div>
                            <div className="text-[8px] text-white/30 uppercase font-black tracking-widest mt-0.5">Actv</div>
                          </div>
                          <div className="bg-black/30 rounded-2xl py-2 px-1 border border-white/5">
                            <div className="text-red-400 font-black text-sm">{user.overdue}</div>
                            <div className="text-[8px] text-white/30 uppercase font-black tracking-widest mt-0.5">Over</div>
                          </div>
                        </div> */}





                        {isAdmin && (
                          <div className="grid grid-cols-3 w-full gap-2 text-center">
                            <div className="bg-black/30 rounded-2xl py-2 px-1 border border-white/5">
                              <div className="text-green-400 font-black text-lg">{user.done}</div>
                              <div className="text-[9px] text-white/40 uppercase font-bold tracking-wider mt-0.5">Done</div>
                            </div>
                            <div className="bg-black/30 rounded-2xl py-2 px-1 border border-white/5">
                              <div className="text-cyan-400 font-black text-lg">{user.active}</div>
                              <div className="text-[9px] text-white/40 uppercase font-bold tracking-wider mt-0.5">Active</div>
                            </div>
                            <div className="bg-black/30 rounded-2xl py-2 px-1 border border-white/5">
                              <div className="text-red-400 font-black text-lg">{user.overdue}</div>
                              <div className="text-[9px] text-white/40 uppercase font-bold tracking-wider mt-0.5">Overdue</div>
                            </div>
                          </div>
                        )}

                        {isAdmin && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectEOM(user);
                            }}
                            className="mt-4 w-full py-2.5 bg-indigo-500/20 hover:bg-indigo-500 text-indigo-400 hover:text-white border border-indigo-500/30 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95"
                          >
                            Select as EOM
                          </button>
                        )}
                        {/* </div> */}
                      </motion.div>
                    );
                  })}
                </div>

                {/* REST OF THE LIST */}
                {isAdmin && rest.length > 0 && (
                  <div className="space-y-3 pt-10 border-t border-white/5">
                    <h4 className="text-[10px] font-black text-white/20 uppercase tracking-[4px] ml-4 mb-6">Other Runners</h4>

                    {rest.map((user, index) => {
                      const isExpanded = expandedUser === user.id;
                      const rank = index + 4;
                      const colors = getAccuracyColors(user.completion);

                      return (
                        <motion.div
                          key={user.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="bg-white/[0.03] border border-white/5 rounded-[24px] overflow-hidden hover:bg-white/[0.06] hover:border-white/10 transition-all duration-300 mb-3"
                        >
                          <div
                            className="p-3 sm:p-4 cursor-pointer select-none"
                            onClick={() => setExpandedUser(isExpanded ? null : user.id)}
                          >
                            {/* Row Header: Rank, Avatar, Name, Accuracy */}
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-black shrink-0 bg-white/5 text-white/40 border border-white/10">
                                {rank}
                              </div>

                              {user.pic ? (
                                <img src={`${IMAGE_BASE_URL}${user.pic}`} alt={user.name} className="w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover ring-2 ring-white/10" />
                              ) : (
                                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/10 flex items-center justify-center text-white font-bold text-xs sm:text-sm ring-2 ring-white/10">
                                  {user.name.charAt(0).toUpperCase()}
                                </div>
                              )}

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="text-white font-bold text-sm sm:text-[15px] truncate pr-2">{user.name}</p>
                                  <span className={`text-[11px] sm:text-xs font-black ${colors.text} shrink-0`}>{user.final_score} PTS</span>
                                </div>
                                <div className="mt-1.5 h-1 sm:h-1.5 bg-black/40 rounded-full overflow-hidden">
                                  <motion.div
                                    className={`h-full rounded-full bg-gradient-to-r ${colors.bar}`}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${Math.min(100, Math.max(0, (user.final_score / (top3[0]?.final_score || 1)) * 100))}%` }}
                                    transition={{ duration: 0.8 }}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Row Footer: Stats & Badge */}
                            <div className="flex items-center justify-between pl-10 sm:pl-11">
                              <div className="flex items-center gap-3 sm:gap-4">
                                <div className="text-center">
                                  <div className="text-green-400 font-black text-[11px] sm:text-sm leading-none">{user.done}</div>
                                  <div className="text-[7px] sm:text-[8px] text-white/30 uppercase font-black tracking-widest mt-0.5">Done</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-indigo-400 font-black text-[11px] sm:text-sm leading-none">{user.active}</div>
                                  <div className="text-[7px] sm:text-[8px] text-white/30 uppercase font-black tracking-widest mt-0.5">Actv</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-red-400 font-black text-[11px] sm:text-sm leading-none">{user.overdue}</div>
                                  <div className="text-[7px] sm:text-[8px] text-white/30 uppercase font-black tracking-widest mt-0.5">Due</div>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="flex flex-col items-end mr-1 sm:mr-3 border-r border-white/10 pr-2 sm:pr-4">
                                  <span className="text-[10px] sm:text-[14px] font-black text-yellow-400 leading-none">
                                    {user.final_score !== undefined ? user.final_score : user.total_points} PTS
                                  </span>
                                  <span className="text-[6px] sm:text-[7px] text-white/40 uppercase tracking-widest font-bold mt-1">
                                    AS OF {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                </div>
                                <div className={`text-[8px] sm:text-[9px] px-2 py-0.5 sm:px-3 sm:py-1 rounded-full border font-black uppercase tracking-wider ${colors.badge}`}>
                                  {colors.label}
                                </div>
                                <div className={`p-1 rounded-full bg-white/5 text-white/40 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                  <ChevronDown size={14} />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* DRILLDOWN CONTENT */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden bg-black/40 border-x border-white/5 mx-6 rounded-b-[24px]"
                              >
                                <div className="p-3 sm:p-6">
                                  {renderUserTasks(user)}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* USER DETAILS MODAL */}

{createPortal(
      <AnimatePresence>
        {selectedTopUser && (
          <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setSelectedTopUser(null)}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl bg-[#0B051D] border border-white/10 rounded-[30px] overflow-hidden flex flex-col shadow-2xl"
            >
              {/* Header */}
              <div className="p-6 border-b border-white/10 flex items-center justify-between bg-white/[0.02]">
                <div className="flex items-center gap-4">
                  {selectedTopUser.pic ? (
                    <img src={`${IMAGE_BASE_URL}${selectedTopUser.pic}`} alt={selectedTopUser.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white/10" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white font-bold text-lg ring-2 ring-white/10">
                      {selectedTopUser.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-black text-white">{selectedTopUser.name}</h3>
                    <div className="text-yellow-400 font-bold text-sm">
                      Final Score: {selectedTopUser.final_score !== undefined ? selectedTopUser.final_score : selectedTopUser.points} PTS
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelectedTopUser(null)} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition">
                  <span className="text-xl leading-none">&times;</span>
                </button>
              </div>

              {/* Content */}
              <div className="p-2 sm:p-4 bg-black/40">
                 {renderUserTasks(selectedTopUser)}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>,
  document.body
)}

      {/* TOP STARS MODAL */}
{createPortal(
      <AnimatePresence>
        {showStarsModal && (
          <div className="fixed inset-0 z-[1002] flex items-end sm:items-center justify-center px-0 sm:px-4">
            <motion.div
              key="stars-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowStarsModal(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              key="stars-content"
              initial={isMobile ? { y: '100%' } : { scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={isMobile ? { y: '100%' } : { scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-2xl bg-[#0B051D] border-t sm:border border-white/10 rounded-t-[40px] sm:rounded-[40px] p-8 sm:p-10 shadow-[0_0_100px_rgba(0,0,0,0.5)] max-h-[90vh] overflow-y-auto custom-scrollbar"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-yellow-400 via-yellow-200 to-yellow-600" />
              <button
                onClick={() => setShowStarsModal(false)}
                className="absolute top-6 right-6 text-white/40 hover:text-white transition z-20"
              >
                <X size={24} />
              </button>

              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-400/10 rounded-full mb-4">
                  <Trophy className="text-yellow-400" size={40} />
                </div>
                <h3 className="text-3xl font-black text-white">Monthly Star Performers</h3>
                <p className="text-white/40 font-bold uppercase tracking-widest text-xs mt-2">{currentMonthLabel}</p>
              </div>

              <div className="space-y-4">
                {top3.length > 0 ? top3.map((star, idx) => {
                  const isExpanded = expandedUser === `modal-${star.id}`;
                  return (
                    <div key={star.id} className="bg-white/5 border border-white/10 rounded-[24px] relative overflow-hidden group transition-all">
                      {idx === 0 && <div className="absolute inset-0 bg-yellow-400/5" />}
                      <div
                        className="flex items-center gap-5 p-4 cursor-pointer"
                        onClick={() => setExpandedUser(isExpanded ? null : `modal-${star.id}`)}
                      >
                        <div className={`relative z-10 w-12 h-12 rounded-full border-2 flex items-center justify-center font-black text-lg shrink-0
                        ${idx === 0 ? 'border-yellow-400 text-yellow-400 bg-yellow-400/10' :
                            idx === 1 ? 'border-slate-300 text-slate-300 bg-slate-300/10' :
                              'border-amber-600 text-amber-600 bg-amber-600/10'}`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 relative z-10">
                          <p className="text-white font-black text-lg flex items-center gap-2">
                            {star.name}
                            {idx === 0 && !isAdmin && <Star className="text-yellow-400 fill-current" size={16} />}
                          </p>
                          {isAdmin && (
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                              <span className="text-indigo-400 text-xs font-black">{star.accuracy}% Accuracy</span>
                              <span className="w-1 h-1 rounded-full bg-white/10" />
                              <span className="text-white/30 text-xs font-bold">{star.points} Points</span>
                              <span className="w-1 h-1 rounded-full bg-white/10" />
                              <span className="text-white/30 text-xs font-bold">{star.done} Tasks Done</span>
                            </div>
                          )}
                        </div>
                        {isAdmin && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectEOM(star);
                            }}
                            className="relative z-10 px-4 py-2 bg-indigo-500/20 hover:bg-indigo-500 text-indigo-400 hover:text-white border border-indigo-500/30 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center gap-2"
                          >
                            <Check size={14} />
                            Choose
                          </button>
                        )}
                        <div className={`p-1 rounded-full bg-white/5 text-white/40 transition-transform ${isExpanded ? 'rotate-180' : ''} ml-2`}>
                          <ChevronDown size={14} />
                        </div>
                      </div>

{createPortal(

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden bg-black/40 border-t border-white/5"
                          >
                            <div className="p-4 space-y-4">
                              {/* Penalties Section */}
                              <div className="space-y-3">
                                <h5 className="text-[10px] font-black text-red-400/60 uppercase tracking-[2px] flex items-center gap-2 mb-2">
                                  <AlertTriangle size={12} /> Penalties & Deductions
                                </h5>
                                <div className="grid grid-cols-3 gap-4">
                                  <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-3 text-center">
                                    <div className="text-red-400 font-black text-lg mb-1">{star.late_days}</div>
                                    <div className="text-[8px] text-white/40 uppercase font-bold">Late Days</div>
                                  </div>
                                  <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-3 text-center">
                                    <div className="text-red-400 font-black text-lg mb-1">{star.early_out_days}</div>
                                    <div className="text-[8px] text-white/40 uppercase font-bold">Early Out</div>
                                  </div>
                                  <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-3 text-center">
                                    <div className="text-red-400 font-black text-lg mb-1">{star.excess_break_days}</div>
                                    <div className="text-[8px] text-white/40 uppercase font-bold">Excess Break</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>,
  document.body
)}
                    </div>
                  );
                }) : (
                  <p className="text-white/20 text-center py-10">No rankings available yet</p>
                )}
              </div>

              <button
                onClick={() => setShowStarsModal(false)}
                className="w-full mt-10 bg-white/10 hover:bg-white/20 text-white font-black py-4 rounded-2xl transition active:scale-95"
              >
                CLOSE VIEW
              </button>

            </motion.div>

          </div>

        )}
      </AnimatePresence>,
  document.body
)}


{createPortal(
      <AnimatePresence>
        {showConfirm && (
          <div className="fixed inset-0 z-[1003] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div
              key="confirm-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowConfirm(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div
              key="confirm-content"
              initial={isMobile ? { y: '100%' } : { scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={isMobile ? { y: '100%' } : { scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-gradient-to-br from-[#1e174a] to-[#0f0c29] border-t sm:border border-white/10 rounded-t-[32px] sm:rounded-[32px] p-8 shadow-2xl overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl" />

              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-400/10 rounded-full mb-6">
                  <Star className="text-yellow-400 fill-current" size={32} />
                </div>
                <h4 className="text-2xl font-black text-white mb-2">Confirm Selection</h4>
                <p className="text-white/60 text-sm leading-relaxed mb-8">
                  Are you sure you want to select <span className="text-yellow-400 font-bold">{pendingPerformer?.name}</span> as the <br />
                  <span className="text-white font-bold uppercase tracking-widest text-[10px]">Employee of the Month</span> for <br />
                  <span className="text-indigo-400 font-bold">{currentMonthLabel}</span>?
                </p>

                <div className="flex gap-4">
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="flex-1 px-6 py-4 bg-white/5 hover:bg-white/10 text-white font-bold rounded-2xl transition-all active:scale-95 border border-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmSelection}
                    className="flex-1 px-6 py-4 bg-indigo-500 hover:bg-indigo-400 text-white font-bold rounded-2xl transition-all active:scale-95 shadow-[0_0_20px_rgba(99,102,241,0.3)]"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>,
  document.body
)}



{createPortal(
      <AnimatePresence>
        {showInstructions && (
          <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center px-0 sm:px-4 py-0 sm:py-10">
            <motion.div
              key="instructions-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowInstructions(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />
            <motion.div
              key="instructions-content"
              initial={isMobile ? { y: '100%' } : { scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={isMobile ? { y: '100%' } : { scale: 0.9, opacity: 0, y: 30 }}
              className="relative z-[999999] w-full max-w-2xl bg-[#0B051D] border-t sm:border border-white/10 rounded-t-[40px] sm:rounded-[40px] p-6 md:p-12 overflow-hidden overflow-y-auto no-scrollbar shadow-[0_0_100px_rgba(99,102,241,0.2)] backdrop-blur-2xl max-h-[90vh] sm:max-h-none"
            >
              {/* Magic Glows */}
              <div className="absolute -top-24 -left-24 w-64 h-64 bg-indigo-500/20 rounded-full blur-[80px]" />
              <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-purple-500/20 rounded-full blur-[80px]" />

              <button
                onClick={() => setShowInstructions(false)}
                className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-full"
              >
                <X size={24} />
              </button>

              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                    <Star className="text-white fill-current" size={28} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-white">Calculation Magic</h3>
                    <p className="text-indigo-300/60 font-bold uppercase tracking-widest text-[10px]">How your points are earned</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Task Points */}
                  <div className="bg-white/5 border border-white/10 rounded-[32px] p-6 space-y-4">
                    <div className="flex items-center gap-2 text-indigo-400 font-black text-xs uppercase tracking-wider">
                      <Calendar size={14} />
                      Deadline Rewards
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-black/20 rounded-2xl border border-white/5 group hover:border-green-500/30 transition">
                        <span className="text-white/70 text-sm">Before Deadline</span>
                        <span className="text-green-400 font-black">+3 Pts</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-black/20 rounded-2xl border border-white/5 group hover:border-indigo-500/30 transition">
                        <span className="text-white/70 text-sm">On Time</span>
                        <span className="text-indigo-400 font-black">+2 Pts</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-black/20 rounded-2xl border border-white/5 group hover:border-red-500/30 transition">
                        <span className="text-white/70 text-sm">Late Completion</span>
                        <span className="text-red-400 font-black">+1 Pt</span>
                      </div>
                    </div>
                  </div>

                  {/* Priority Bonus */}
                  <div className="bg-white/5 border border-white/10 rounded-[32px] p-6 space-y-4">
                    <div className="flex items-center gap-2 text-purple-400 font-black text-xs uppercase tracking-wider">
                      <Target size={14} />
                      Priority Bonus
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-black/20 rounded-2xl border border-white/5 group hover:border-purple-500/30 transition">
                        <span className="text-white/70 text-sm">High Priority</span>
                        <span className="text-purple-400 font-black">+3 Pts</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-black/20 rounded-2xl border border-white/5 group hover:border-blue-500/30 transition">
                        <span className="text-white/70 text-sm">Medium Priority</span>
                        <span className="text-blue-400 font-black">+2 Pts</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-black/20 rounded-2xl border border-white/5 group hover:border-slate-500/30 transition">
                        <span className="text-white/70 text-sm">Low Priority</span>
                        <span className="text-slate-400 font-black">+1 Pt</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div className="mt-6 bg-red-500/5 border border-red-500/10 rounded-[32px] p-6 space-y-4">
                  <div className="flex items-center gap-2 text-red-400 font-black text-xs uppercase tracking-wider">
                    <MinusCircle size={14} />
                    Attendance Deductions
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 bg-black/40 rounded-2xl border border-red-500/10 group hover:border-red-500/30 transition">
                      <div className="flex flex-col">
                        <span className="text-white/80 text-sm font-bold">Late Entry & Early Exit </span>
                        <span className="text-white/30 text-[10px]">Arrival after 10:00 AM</span>
                        <span className="text-white/30 text-[10px]">Exit before 6:30 PM or &lt;9hrs</span>

                      </div>
                      <span className="text-red-400 font-black">-1 Pt</span>
                    </div>
                    {/* <div className="flex items-center justify-between p-4 bg-black/40 rounded-2xl border border-red-500/10 group hover:border-red-500/30 transition">
                      <div className="flex flex-col">
                        <span className="text-white/80 text-sm font-bold">Early Exit / Short Hours</span>
                        <span className="text-white/30 text-[10px]">Exit before 6:30 PM or &lt;9hrs</span>
                      </div>
                      <span className="text-red-400 font-black">-1 Pt</span>
                    </div> */}
                    <div className="flex items-center justify-between p-4 bg-black/40 rounded-2xl border border-red-500/10 group hover:border-red-500/30 transition">
                      <div className="flex flex-col">
                        <span className="text-white/80 text-sm font-bold">Excessive Break</span>
                        <span className="text-white/30 text-[10px]">Total break time &gt; 50 minutes</span>
                      </div>
                      <span className="text-red-400 font-black">-1 Pt</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full mt-10 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-black py-4 rounded-2xl transition active:scale-95 shadow-xl shadow-indigo-500/20"
                >
                  GOT IT, THANKS!
                </button>
              </div>

            </motion.div>
          </div>


        )}
      </AnimatePresence>,
  document.body
)}
    </>
  );
}




